/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/Manual/SaveDraftEndpointButton" />
import React = require("react");
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { EndpointsEditorActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsCreator";
import { NavigationActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/NavigationActionsCreator";
import { ActionsCreatorInstanceIds } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { EndpointsEditorStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointsEditorStore";
import { cleanEmptyValues } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import { AzureEndpointConstants } from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { shallowCompare } from "VSS/Core/Util/Object";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button, IButtonProps } from "VSSUI/Button";
import { EndpointSaveProgressStore, EndpointSaveProgressStoreEvents, createEndpointSaveProgressStore, deleteEndpointSaveProgressStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointSaveProgressStore";

interface ISaveDraftEndpointButtonProps extends IButtonProps {
    isUpdateFlow: boolean;
    closeUponSuccess: boolean;
}

export class SaveDraftEndpointButton extends VssComponent<ISaveDraftEndpointButtonProps> {

    private readonly _originalEndpoint: ServiceEndpoint;
    private readonly _originalIsOpenToAllPipelines: boolean;

    private readonly _endpointEditorStore: EndpointsEditorStore;
    private readonly _endpointEditorActionCreator: EndpointsEditorActionsCreator;
    private readonly _navigationActionsCreator: NavigationActionsCreator;
    private readonly _endpointSaveProgressStore: EndpointSaveProgressStore;

    constructor(props: ISaveDraftEndpointButtonProps, context: IVssComponentContext) {
        super(props, context);

        this._endpointEditorStore = StoreManager.GetStore(EndpointsEditorStore, ActionsCreatorInstanceIds.EndpointsEditorStoreInstanceId);

        this._endpointEditorActionCreator = ActionCreatorManager.GetActionCreator<EndpointsEditorActionsCreator>(EndpointsEditorActionsCreator, ActionsCreatorInstanceIds.EndpointsEditorActionsCreatorInstanceId);
        this._navigationActionsCreator = ActionCreatorManager.GetActionCreator<NavigationActionsCreator>(NavigationActionsCreator, ActionsCreatorInstanceIds.NavigationActionsCreatorInstanceId);

        this._endpointSaveProgressStore = createEndpointSaveProgressStore();
        this._endpointSaveProgressStore.addListener(EndpointSaveProgressStoreEvents.Completed, this._onSaveEndpointCompleted);

        this._originalEndpoint = this._endpointEditorStore.getEndpointClone();
        this._originalIsOpenToAllPipelines = this._endpointEditorStore.isOpenToAllPipelines();
    }

    public componentWillUnmount(): void {
        this._endpointSaveProgressStore.removeListener(EndpointSaveProgressStoreEvents.Succeeded, this._onSaveEndpointSuccess);
        this._endpointSaveProgressStore.removeListener(EndpointSaveProgressStoreEvents.Completed, this._onSaveEndpointCompleted);
        deleteEndpointSaveProgressStore(this._endpointSaveProgressStore);
    }

    public render(): JSX.Element {
        return (<Button
            className="back-button"
            text={Resources.SaveAsDraftText}
            onClick={this._saveDraftEndpoint}
            {...this.props} />);
    }

    private _saveDraftEndpoint = (): void => {
        const endpoint = this._endpointEditorStore.getEndpointClone();
        const openToAllPipelinesFlag = this._endpointEditorStore.isOpenToAllPipelines();
        if (this.endpointChanged(endpoint) || openToAllPipelinesFlag !== this._originalIsOpenToAllPipelines) {
            // Drafts are legal as long as values are either set correctly or not set at all
            cleanEmptyValues(endpoint.data);
            cleanEmptyValues(endpoint.authorization.parameters);
            endpoint.data[AzureEndpointConstants.IsDraft] = "true";
            this._endpointSaveProgressStore.addListener(EndpointSaveProgressStoreEvents.Succeeded, this._onSaveEndpointSuccess);
            this._endpointEditorActionCreator.customSaveServiceEndpoint(endpoint, openToAllPipelinesFlag, this.props.isUpdateFlow);
        } else {
            this.closePanel();
        }
    }

    private endpointChanged(endpoint: ServiceEndpoint): boolean {
        const original = this._originalEndpoint;

        if (!shallowCompare(endpoint.authorization.parameters, original.authorization.parameters)) {
            return true;
        }

        if (!shallowCompare(endpoint.data, original.data)) {
            return true;
        }

        if (!shallowCompare(endpoint.name, original.name)) {
            return true;
        }

        return !shallowCompare(endpoint.description, original.description);
    }

    private _onSaveEndpointSuccess = (): void => {
        this.closePanel();
    }

    private _onSaveEndpointCompleted = (): void => {
        this._endpointSaveProgressStore.removeListener(EndpointSaveProgressStoreEvents.Succeeded, this._onSaveEndpointSuccess);
    }

    private closePanel(): void {
        if (this.props.closeUponSuccess) {
            this._navigationActionsCreator.closePanel();
        }
    }
}
